// src/pages/trading-portal/OptionsContractManagement/StrategyAnalyzerPage.js
// Options Strategy Analyzer — Interactive P&L heatmap matching optionstrat.com design

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Box,
    Typography,
    Alert,
    Slider,
    CircularProgress,
    Autocomplete,
    TextField,
    IconButton,
    Tooltip,
    Chip,
    Button,
} from '@mui/material';
import {
    ChevronLeft as ChevronLeftIcon,
    ChevronRight as ChevronRightIcon,
    RestartAlt as ResetIcon,
    TableChart as TableChartIcon,
    ShowChart as GraphIcon,
} from '@mui/icons-material';
import axiosInstance from '../../../utils/axiosInstance';
import { hasPermission } from '../../../utils/permissionUtils';
import { StrategyTypeLabels } from '../stockTradingEnums';
import {
    generatePnLMatrix,
    transformToPercentOfMaxRisk,
    transformToProfitLossPercent,
    transformToContractValue,
} from '../../../utils/blackScholes';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#f59e0b';

// Dark theme palette (matching optionstrat.com)
const DARK_BG = '#0c1222';
const DARK_SURFACE = '#151e2f';
const DARK_HEADER = '#1a2438';
const DARK_BORDER = '#2a3548';
const DARK_TEXT = '#e2e8f0';
const DARK_TEXT_MUTED = '#8896aa';
const DARK_TEXT_DIM = '#5a6a80';

// Day-of-week labels (matching screenshot: S, M, T, W, Th, F, S)
const DAY_LABELS = ['S', 'M', 'T', 'W', 'Th', 'F', 'S'];
const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// ─── Color gradient: vivid green-to-red matching optionstrat ────────────────

function getCellColor(value, maxPositive, maxNegative) {
    if (maxPositive === 0 && maxNegative === 0) {
        return { bg: DARK_SURFACE, text: DARK_TEXT_MUTED };
    }

    // Near-zero band — show dark neutral
    const maxAbsolute = Math.max(Math.abs(maxPositive), Math.abs(maxNegative));
    const threshold = maxAbsolute * 0.005;
    if (Math.abs(value) < threshold) {
        return { bg: '#111a2e', text: DARK_TEXT_MUTED };
    }

    if (value > 0) {
        const intensity = Math.min(Math.abs(value) / (Math.abs(maxPositive) || 1), 1);
        // Green gradient: dark forest → medium green → vivid green (matching optionstrat)
        const r = Math.round(10 + (20 - 10) * intensity);
        const g = Math.round(30 + (180 - 30) * intensity);
        const b = Math.round(20 + (60 - 20) * intensity);
        return {
            bg: `rgb(${r}, ${g}, ${b})`,
            text: intensity > 0.15 ? '#ffffff' : '#86efac',
        };
    } else {
        const intensity = Math.min(Math.abs(value) / (Math.abs(maxNegative) || 1), 1);
        // Red gradient: dark maroon → warm red → vivid red (matching optionstrat)
        const r = Math.round(35 + (200 - 35) * intensity);
        const g = Math.round(15 + (30 - 15) * (1 - intensity));
        const b = Math.round(15 + (25 - 15) * (1 - intensity));
        return {
            bg: `rgb(${r}, ${g}, ${b})`,
            text: intensity > 0.15 ? '#ffffff' : '#fca5a5',
        };
    }
}

// ─── Format cell value based on view mode ───────────────────────────────────

function formatCellValue(value, viewMode) {
    if (value == null) return '-';
    switch (viewMode) {
        case 'pnl_dollar':
            return value >= 0 ? `$${value.toFixed(0)}` : `-$${Math.abs(value).toFixed(0)}`;
        case 'contract_value':
            return `$${value.toFixed(0)}`;
        case 'pnl_percent':
        case 'pct_max_risk':
        default:
            return `${value.toFixed(0)}%`;
    }
}

// ─── Safe date parsing helper ────────────────────────────────────────────────
function safeParseDateStr(dateStr) {
    if (!dateStr) return new Date(NaN);
    const datePart = String(dateStr).substring(0, 10);
    return new Date(datePart + 'T12:00:00');
}

// ─── Build month groups from date strings ───────────────────────────────────

function buildMonthGroups(dates) {
    const groups = [];
    let currentMonth = null;
    let currentGroup = null;

    dates.forEach((dateStr) => {
        const d = safeParseDateStr(dateStr);
        const monthKey = `${d.getFullYear()}-${d.getMonth()}`;

        if (monthKey !== currentMonth) {
            currentMonth = monthKey;
            // Show year suffix for dates outside the current year (like optionstrat: "Jan '27")
            const currentYear = new Date().getFullYear();
            const yearSuffix = d.getFullYear() !== currentYear ? ` '${String(d.getFullYear()).slice(-2)}` : '';
            currentGroup = {
                label: `${MONTH_NAMES[d.getMonth()]}${yearSuffix}`,
                count: 1,
            };
            groups.push(currentGroup);
        } else {
            currentGroup.count++;
        }
    });

    return groups;
}

// ─── P&L Payoff Graph (SVG) ─────────────────────────────────────────────────

function PayoffGraph({ prices, matrix, underlyingPrice }) {
    if (!prices || !matrix || prices.length === 0 || matrix.length === 0) return null;

    const width = 900;
    const height = 400;
    const padL = 70, padR = 30, padT = 30, padB = 50;
    const chartW = width - padL - padR;
    const chartH = height - padT - padB;

    // Use the last column (at expiration) for the payoff line
    const lastColIdx = matrix[0].length - 1;
    // Also show current-date payoff (first column)
    const firstColIdx = 0;

    const expirationPayoff = prices.map((_, rowIdx) => matrix[rowIdx][lastColIdx]);
    const currentPayoff = prices.map((_, rowIdx) => matrix[rowIdx][firstColIdx]);

    const allVals = [...expirationPayoff, ...currentPayoff];
    const minVal = Math.min(...allVals);
    const maxVal = Math.max(...allVals);
    const valRange = maxVal - minVal || 1;

    const minPrice = Math.min(...prices);
    const maxPrice = Math.max(...prices);
    const priceRange = maxPrice - minPrice || 1;

    const toX = (p) => padL + ((p - minPrice) / priceRange) * chartW;
    const toY = (v) => padT + chartH - ((v - minVal) / valRange) * chartH;

    // Build SVG path for a data series (prices are high-to-low, reverse for left-to-right)
    const buildPath = (payoff) => {
        const sortedData = prices.map((p, i) => ({ p, v: payoff[i] })).sort((a, b) => a.p - b.p);
        return sortedData.map((d, i) => `${i === 0 ? 'M' : 'L'} ${toX(d.p).toFixed(1)} ${toY(d.v).toFixed(1)}`).join(' ');
    };

    // Split expiration payoff into green (above 0) and red (below 0) segments
    const sortedExpData = prices.map((p, i) => ({ p, v: expirationPayoff[i] })).sort((a, b) => a.p - b.p);

    // Build green/red fill areas
    const zeroY = toY(0);
    const greenFillPoints = [];
    const redFillPoints = [];

    for (const d of sortedExpData) {
        const x = toX(d.p);
        const y = toY(d.v);
        if (d.v >= 0) {
            greenFillPoints.push(`${x.toFixed(1)},${y.toFixed(1)}`);
        }
        if (d.v <= 0) {
            redFillPoints.push(`${x.toFixed(1)},${y.toFixed(1)}`);
        }
    }

    // Y-axis labels
    const yTickCount = 6;
    const yStep = valRange / yTickCount;
    const yTicks = [];
    for (let i = 0; i <= yTickCount; i++) {
        yTicks.push(minVal + yStep * i);
    }

    // X-axis labels
    const xTickCount = 8;
    const xStep = priceRange / xTickCount;
    const xTicks = [];
    for (let i = 0; i <= xTickCount; i++) {
        xTicks.push(minPrice + xStep * i);
    }

    return (
        <Box sx={{ p: 2, overflow: 'auto' }}>
            <svg viewBox={`0 0 ${width} ${height}`} style={{ width: '100%', maxHeight: 420, fontFamily }}>
                {/* Grid lines */}
                {yTicks.map((v, i) => (
                    <line key={`yg${i}`} x1={padL} y1={toY(v)} x2={width - padR} y2={toY(v)}
                        stroke={DARK_BORDER} strokeWidth={0.5} />
                ))}

                {/* Zero line */}
                {minVal < 0 && maxVal > 0 && (
                    <line x1={padL} y1={zeroY} x2={width - padR} y2={zeroY}
                        stroke="#475569" strokeWidth={1.5} strokeDasharray="4 3" />
                )}

                {/* Current price vertical line */}
                <line x1={toX(underlyingPrice)} y1={padT} x2={toX(underlyingPrice)} y2={padT + chartH}
                    stroke={ACCENT} strokeWidth={1} strokeDasharray="3 3" />
                <text x={toX(underlyingPrice)} y={padT - 8} textAnchor="middle"
                    fill={ACCENT} fontSize="10" fontWeight="600">
                    ${underlyingPrice.toFixed(0)}
                </text>

                {/* Green fill area (profit at expiration) */}
                {greenFillPoints.length > 1 && (
                    <polygon
                        points={`${greenFillPoints.join(' ')} ${toX(sortedExpData.find((d) => d.v >= 0 && sortedExpData[sortedExpData.indexOf(d) + 1]?.v < 0)?.p || sortedExpData[sortedExpData.length - 1].p).toFixed(1)},${zeroY.toFixed(1)} ${toX(sortedExpData.find((d) => d.v >= 0)?.p || sortedExpData[0].p).toFixed(1)},${zeroY.toFixed(1)}`}
                        fill="rgba(34, 197, 94, 0.12)" />
                )}

                {/* Current date payoff line (dimmer) */}
                <path d={buildPath(currentPayoff)} fill="none"
                    stroke="#6366f1" strokeWidth={1.5} strokeDasharray="4 2" opacity={0.6} />

                {/* Expiration payoff line (bold) */}
                <path d={buildPath(expirationPayoff)} fill="none"
                    stroke="#22c55e" strokeWidth={2.5} />

                {/* Y-axis labels */}
                {yTicks.map((v, i) => (
                    <text key={`yl${i}`} x={padL - 8} y={toY(v) + 4} textAnchor="end"
                        fill={DARK_TEXT_MUTED} fontSize="10" fontWeight="500">
                        {v >= 0 ? `$${v.toFixed(0)}` : `-$${Math.abs(v).toFixed(0)}`}
                    </text>
                ))}

                {/* X-axis labels */}
                {xTicks.map((p, i) => (
                    <text key={`xl${i}`} x={toX(p)} y={padT + chartH + 20} textAnchor="middle"
                        fill={DARK_TEXT_MUTED} fontSize="10" fontWeight="500">
                        ${p.toFixed(0)}
                    </text>
                ))}

                {/* Legend */}
                <line x1={padL + 10} y1={padT + 10} x2={padL + 30} y2={padT + 10}
                    stroke="#22c55e" strokeWidth={2.5} />
                <text x={padL + 35} y={padT + 14} fill={DARK_TEXT_MUTED} fontSize="10">At Expiration</text>

                <line x1={padL + 130} y1={padT + 10} x2={padL + 150} y2={padT + 10}
                    stroke="#6366f1" strokeWidth={1.5} strokeDasharray="4 2" />
                <text x={padL + 155} y={padT + 14} fill={DARK_TEXT_MUTED} fontSize="10">Today</text>
            </svg>
        </Box>
    );
}

// ─── Main Component ─────────────────────────────────────────────────────────

const StrategyAnalyzerPage = () => {
    // Permissions
    const [canView, setCanView] = useState(false);

    // Position selection
    const [positions, setPositions] = useState([]);
    const [selectedPosition, setSelectedPosition] = useState(null);
    const [positionsLoading, setPositionsLoading] = useState(false);

    // Analyzer data (from backend)
    const [analyzerData, setAnalyzerData] = useState(null);
    const [dataLoading, setDataLoading] = useState(false);
    const [error, setError] = useState(null);

    // Slider controls
    const [priceRange, setPriceRange] = useState([-23, 23]);
    const [ivMultiplier, setIvMultiplier] = useState(1.0);

    // View mode: 'pnl_dollar' | 'pnl_percent' | 'contract_value' | 'pct_max_risk'
    const [viewMode, setViewMode] = useState('pnl_dollar');

    // Display type: 'table' | 'graph'
    const [displayType, setDisplayType] = useState('table');

    // Summary bar panel: 0 = Greeks, 1 = Strategy Summary
    const [summaryPanel, setSummaryPanel] = useState(0);

    // ── Permission check ────────────────────────────────────────────────
    useEffect(() => {
        const checkPermissions = async () => {
            const view = await hasPermission('tr-ocm-strategy-analyzer', 'view');
            setCanView(view);
        };
        checkPermissions();
    }, []);

    // ── Fetch positions list ────────────────────────────────────────────
    const fetchPositions = useCallback(async () => {
        try {
            setPositionsLoading(true);
            const response = await axiosInstance.get(
                '/api/options-contract-management/positions?per_page=100'
            );
            const data = response.data?.data || [];
            setPositions(data);
        } catch (err) {
            console.error('Failed to fetch positions:', err);
        } finally {
            setPositionsLoading(false);
        }
    }, []);

    useEffect(() => {
        if (canView) fetchPositions();
    }, [canView, fetchPositions]);

    // ── Fetch analyzer data for selected position ───────────────────────
    const fetchAnalyzerData = useCallback(async (positionId) => {
        try {
            setDataLoading(true);
            setError(null);
            const response = await axiosInstance.get(
                `/api/options-contract-management/positions/${positionId}/strategy-analyzer`
            );
            if (response.data?.success) {
                setAnalyzerData(response.data.data);
            } else {
                setError(response.data?.message || 'Failed to load analyzer data.');
            }
        } catch (err) {
            console.error('Failed to fetch analyzer data:', err);
            setError(err.response?.data?.message || 'Failed to load analyzer data.');
        } finally {
            setDataLoading(false);
        }
    }, []);

    useEffect(() => {
        if (selectedPosition?.id) {
            fetchAnalyzerData(selectedPosition.id);
        } else {
            setAnalyzerData(null);
        }
    }, [selectedPosition, fetchAnalyzerData]);

    // ── Compute P&L matrix ──────────────────────────────────────────────
    const matrixResult = useMemo(() => {
        if (!analyzerData || !analyzerData.legs || analyzerData.legs.length === 0) return null;

        const { legs, underlying_price } = analyzerData;

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const expirationDates = legs
            .filter((l) => l.expiration_date)
            .map((l) => safeParseDateStr(l.expiration_date));

        const furthestExpiration =
            expirationDates.length > 0
                ? new Date(Math.max(...expirationDates.map((d) => d.getTime())))
                : new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000);

        const raw = generatePnLMatrix({
            legs,
            underlyingPrice: underlying_price,
            priceRange: { min: priceRange[0], max: priceRange[1] },
            priceSteps: 22,
            dateRange: { start: today, end: furthestExpiration },
            riskFreeRate: 0.05,
            ivMultiplier,
        });

        const avgIV =
            legs.reduce((sum, l) => sum + (l.implied_volatility || 0.3), 0) / legs.length;

        // Always use client-computed Greeks (uses current underlying price, IV slider,
        // and real-time DTE — backend Greeks are often null/stale in DB)
        return { ...raw, netGreeks: raw.netGreeks, avgIV };
    }, [analyzerData, priceRange, ivMultiplier]);

    // ── Transform matrix based on view mode ─────────────────────────────
    const displayData = useMemo(() => {
        if (!matrixResult || matrixResult.dates.length === 0) return null;

        const { matrix, maxLoss } = matrixResult;
        let displayMatrix;

        switch (viewMode) {
            case 'pct_max_risk': {
                // If maxLoss is 0 or positive (all profitable), scale against maxProfit
                const effectiveMaxLoss = maxLoss < 0 ? maxLoss : -(matrixResult.maxProfit || 1);
                displayMatrix = transformToPercentOfMaxRisk(matrix, effectiveMaxLoss);
                break;
            }
            case 'pnl_percent':
                displayMatrix = transformToProfitLossPercent(
                    matrix,
                    analyzerData?.position?.net_cost_basis || 1
                );
                break;
            case 'contract_value':
                // net_premium is already total dollars (backend multiplies by 100)
                displayMatrix = transformToContractValue(
                    matrix,
                    Math.abs(analyzerData?.position?.net_premium || 0)
                );
                break;
            case 'pnl_dollar':
            default:
                displayMatrix = matrix;
                break;
        }

        let maxPositive = 0;
        let maxNegative = 0;
        for (const row of displayMatrix) {
            for (const val of row) {
                if (val > maxPositive) maxPositive = val;
                if (val < maxNegative) maxNegative = val;
            }
        }

        return { displayMatrix, maxPositive, maxNegative };
    }, [matrixResult, viewMode, analyzerData]);

    // ── Month groups for header ─────────────────────────────────────────
    const monthGroups = useMemo(() => {
        if (!matrixResult?.dates) return [];
        return buildMonthGroups(matrixResult.dates);
    }, [matrixResult?.dates]);

    // ── Computed summary values (max profit/loss/breakeven from matrix) ──
    const summaryValues = useMemo(() => {
        if (!matrixResult || !analyzerData) return null;

        const pos = analyzerData.position || {};
        const netPremium = pos.net_premium || 0;
        // Positive net_premium = BUY cost > SELL proceeds = DEBIT
        // Negative net_premium = SELL proceeds > BUY cost = CREDIT
        const isDebit = netPremium > 0;

        // Max profit / max loss — prefer backend if stored, else derive from matrix
        const hasBackendMaxProfit = pos.max_profit != null;
        const hasBackendMaxLoss = pos.max_loss != null;
        const maxProfit = hasBackendMaxProfit ? Math.abs(pos.max_profit) : matrixResult.maxProfit;
        const maxLoss = hasBackendMaxLoss ? Math.abs(pos.max_loss) : Math.abs(matrixResult.maxLoss);

        // If loss is at the boundary of price range, it may be unlimited
        const { prices, matrix } = matrixResult;
        const lastColIdx = matrix[0]?.length - 1 || 0;
        const expirationPnL = prices.map((_, rowIdx) => matrix[rowIdx][lastColIdx]);

        // Check if max loss occurs at the edge rows (indicates potentially unlimited risk)
        const minPnLIdx = expirationPnL.reduce((minIdx, val, idx, arr) => val < arr[minIdx] ? idx : minIdx, 0);
        const maxLossAtEdge = !hasBackendMaxLoss && (minPnLIdx === 0 || minPnLIdx === prices.length - 1);

        // Breakeven prices — interpolate from expiration P&L column
        let breakevens = pos.breakeven_prices;
        if (!breakevens || !Array.isArray(breakevens) || breakevens.length === 0) {
            breakevens = [];
            for (let i = 0; i < prices.length - 1; i++) {
                if ((expirationPnL[i] >= 0 && expirationPnL[i + 1] < 0) ||
                    (expirationPnL[i] < 0 && expirationPnL[i + 1] >= 0)) {
                    const p1 = prices[i], p2 = prices[i + 1];
                    const v1 = expirationPnL[i], v2 = expirationPnL[i + 1];
                    const be = p1 + (0 - v1) * (p2 - p1) / (v2 - v1);
                    breakevens.push(parseFloat(be.toFixed(2)));
                }
            }
        }

        // Chance of profit — probability-weighted from log-normal distribution
        let chanceOfProfit = null;
        const underlyingPrice = analyzerData.underlying_price || 0;
        const legs = analyzerData.legs || [];
        if (underlyingPrice > 0 && legs.length > 0 && expirationPnL.length > 1) {
            const avgIV = legs.reduce((sum, l) => sum + (l.implied_volatility || 0.3), 0) / legs.length * ivMultiplier;
            const furthestDte = Math.max(...legs.map(l => l.dte || 30));
            const T = furthestDte / 365;
            const sigma = avgIV;

            if (T > 0 && sigma > 0) {
                let profitProb = 0;
                let totalProb = 0;
                for (let i = 0; i < prices.length - 1; i++) {
                    const midPrice = (prices[i] + prices[i + 1]) / 2;
                    const width = Math.abs(prices[i] - prices[i + 1]);
                    // Log-normal PDF
                    const x = Math.log(midPrice / underlyingPrice);
                    const mu = -0.5 * sigma * sigma * T;
                    const std = sigma * Math.sqrt(T);
                    const prob = Math.exp(-0.5 * ((x - mu) / std) ** 2) / (midPrice * std * Math.sqrt(2 * Math.PI)) * width;
                    totalProb += prob;
                    const midPnL = (expirationPnL[i] + expirationPnL[i + 1]) / 2;
                    if (midPnL > 0) profitProb += prob;
                }
                if (totalProb > 0) {
                    chanceOfProfit = Math.round((profitProb / totalProb) * 100);
                }
            }
        }

        return { netPremium, isDebit, maxProfit, maxLoss, maxLossAtEdge, breakevens, chanceOfProfit };
    }, [matrixResult, analyzerData, ivMultiplier]);

    // ── Permission gate ─────────────────────────────────────────────────
    if (!canView) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error" sx={{ fontFamily }}>
                    You do not have permission to view the Strategy Analyzer.
                </Alert>
            </Box>
        );
    }

    // ── Cell style helper ───────────────────────────────────────────────
    const cellBase = {
        fontFamily,
        fontSize: '0.72rem',
        fontWeight: 600,
        textAlign: 'center',
        padding: '4px 2px',
        whiteSpace: 'nowrap',
        borderRight: `1px solid ${DARK_BG}`,
        borderBottom: `1px solid ${DARK_BG}`,
        minWidth: 52,
    };

    const headerCellBase = {
        fontFamily,
        fontSize: '0.68rem',
        fontWeight: 600,
        textAlign: 'center',
        padding: '6px 2px',
        whiteSpace: 'nowrap',
        color: DARK_TEXT_MUTED,
        backgroundColor: DARK_HEADER,
        borderRight: `1px solid ${DARK_BORDER}`,
        borderBottom: `1px solid ${DARK_BORDER}`,
        position: 'sticky',
        top: 0,
        zIndex: 2,
        minWidth: 52,
    };

    // ── Render ──────────────────────────────────────────────────────────
    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            {/* ── Header ──────────────────────────────────────────────── */}
            <Box
                sx={{
                    background: 'linear-gradient(135deg, #1e1e2e 0%, #2d1b4e 100%)',
                    borderRadius: 3,
                    p: 4,
                    mb: 3,
                    position: 'relative',
                    overflow: 'hidden',
                }}
            >
                <Box sx={{ position: 'absolute', top: -40, right: -40, width: 150, height: 150, borderRadius: '50%', background: `${ACCENT}15` }} />
                <Box sx={{ position: 'absolute', bottom: -20, left: '30%', width: 100, height: 100, borderRadius: '50%', background: `${ACCENT}10` }} />
                <Typography variant="h4" fontWeight={700} sx={{ color: '#fff', fontFamily, position: 'relative', zIndex: 1 }}>
                    Strategy Analyzer
                </Typography>
                <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.7)', fontFamily, mt: 1, position: 'relative', zIndex: 1 }}>
                    Interactive P&L heatmap and Greeks analysis for option strategies
                </Typography>
            </Box>

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1600, mx: 'auto' }}>
                {/* ── Error Alert ──────────────────────────────────────── */}
                {error && (
                    <Alert
                        severity="error"
                        sx={{ mb: 3, borderRadius: '12px', fontFamily }}
                        action={
                            <Button color="inherit" size="small" onClick={() => selectedPosition && fetchAnalyzerData(selectedPosition.id)} sx={{ fontFamily, textTransform: 'none', fontWeight: 600 }}>
                                Retry
                            </Button>
                        }
                    >
                        {error}
                    </Alert>
                )}

                {/* ── Position Selector ────────────────────────────────── */}
                <Box sx={{ mb: 3, maxWidth: 500 }}>
                    <Autocomplete
                        options={positions}
                        loading={positionsLoading}
                        value={selectedPosition}
                        onChange={(_, newVal) => setSelectedPosition(newVal)}
                        getOptionLabel={(option) => {
                            const strategy = option.strategy_type
                                ? StrategyTypeLabels?.[option.strategy_type] || option.strategy_type
                                : '';
                            return `${option.symbol || 'Unknown'} \u2014 ${option.position_name || strategy || `Position #${option.id}`}`;
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Select Position"
                                placeholder="Choose a position to analyze..."
                                variant="outlined"
                                InputProps={{ ...params.InputProps, sx: { fontFamily, borderRadius: '12px' } }}
                                InputLabelProps={{ sx: { fontFamily } }}
                            />
                        )}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                    />
                </Box>

                {/* ── Loading ──────────────────────────────────────────── */}
                {dataLoading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
                        <CircularProgress sx={{ color: ACCENT }} />
                    </Box>
                )}

                {/* ── Empty State ──────────────────────────────────────── */}
                {!dataLoading && !analyzerData && !error && (
                    <Box sx={{ textAlign: 'center', py: 10, bgcolor: DARK_BG, borderRadius: '16px', border: `1px solid ${DARK_BORDER}` }}>
                        <TableChartIcon sx={{ fontSize: 60, color: DARK_TEXT_DIM, mb: 2 }} />
                        <Typography sx={{ fontFamily, color: DARK_TEXT_MUTED, fontSize: '1rem', fontWeight: 600 }}>
                            Select a position to analyze
                        </Typography>
                        <Typography sx={{ fontFamily, color: DARK_TEXT_DIM, fontSize: '0.85rem', mt: 1 }}>
                            Choose an existing options position to view its P&L heatmap
                        </Typography>
                    </Box>
                )}

                {/* ── Analyzer Content ─────────────────────────────────── */}
                {!dataLoading && analyzerData && matrixResult && displayData && (
                    <Box sx={{ bgcolor: DARK_BG, borderRadius: '16px', overflow: 'hidden', border: `1px solid ${DARK_BORDER}` }}>

                        {/* ── 1. EXPIRATION header + Table/Graph toggle ──── */}
                        {(() => {
                            const legs = analyzerData?.legs || [];
                            const expirations = legs.filter((l) => l.expiration_date).map((l) => {
                                const exp = safeParseDateStr(l.expiration_date);
                                const now = new Date();
                                now.setHours(12, 0, 0, 0);
                                const dte = Math.max(Math.ceil((exp.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)), 0);
                                return dte;
                            });
                            const nearestDte = expirations.length > 0 ? Math.min(...expirations) : 0;
                            return (
                                <Box sx={{ px: 2, py: 1, borderBottom: `1px solid ${DARK_BORDER}`, bgcolor: DARK_BG, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <Typography sx={{ fontFamily, fontSize: '0.8rem', fontWeight: 700, color: DARK_TEXT, letterSpacing: '0.04em' }}>
                                        EXPIRATION: <span style={{ color: ACCENT }}>{nearestDte}d</span>
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                                        {[
                                            { type: 'table', label: 'Table', icon: <TableChartIcon sx={{ fontSize: 15, mr: 0.5 }} /> },
                                            { type: 'graph', label: 'Graph', icon: <GraphIcon sx={{ fontSize: 15, mr: 0.5 }} /> },
                                        ].map((tab) => {
                                            const isActive = displayType === tab.type;
                                            return (
                                                <Chip
                                                    key={tab.type}
                                                    label={<Box sx={{ display: 'flex', alignItems: 'center' }}>{tab.icon}{tab.label}</Box>}
                                                    onClick={() => setDisplayType(tab.type)}
                                                    sx={{
                                                        fontFamily, fontSize: '0.73rem', fontWeight: isActive ? 700 : 500, height: 28,
                                                        bgcolor: isActive ? '#3b82f6' : 'transparent',
                                                        color: isActive ? '#fff' : DARK_TEXT_MUTED,
                                                        border: isActive ? 'none' : `1px solid ${DARK_BORDER}`,
                                                        borderRadius: '6px', cursor: 'pointer',
                                                        '&:hover': { bgcolor: isActive ? '#3b82f6' : DARK_BORDER },
                                                    }}
                                                />
                                            );
                                        })}
                                    </Box>
                                </Box>
                            );
                        })()}

                        {/* ── STRIKE visual row ──────────────────────────── */}
                        {(() => {
                            const legs = analyzerData?.legs || [];
                            const underlyingPrice = analyzerData?.underlying_price || 0;
                            if (!underlyingPrice || legs.length === 0) return null;

                            // Price range for the number line
                            const minP = underlyingPrice * (1 + priceRange[0] / 100);
                            const maxP = underlyingPrice * (1 + priceRange[1] / 100);
                            const range = maxP - minP;
                            if (range <= 0) return null;

                            // Build tick marks at round intervals
                            const rawStep = range / 10;
                            const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));
                            const step = Math.ceil(rawStep / magnitude) * magnitude;
                            const tickStart = Math.ceil(minP / step) * step;
                            const ticks = [];
                            for (let t = tickStart; t <= maxP; t += step) {
                                ticks.push(t);
                            }

                            const pctPos = (price) => ((price - minP) / range) * 100;

                            return (
                                <Box sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${DARK_BORDER}`, bgcolor: DARK_BG }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <Typography sx={{ fontFamily, fontSize: '0.75rem', fontWeight: 700, color: DARK_TEXT_MUTED, minWidth: 55, letterSpacing: '0.04em' }}>
                                            STRIKE:
                                        </Typography>
                                        <Box sx={{ flex: 1, position: 'relative', height: 40 }}>
                                            {/* Number line */}
                                            <Box sx={{ position: 'absolute', top: 20, left: 0, right: 0, height: 1, bgcolor: DARK_BORDER }} />

                                            {/* Tick marks */}
                                            {ticks.map((t) => (
                                                <Box key={t} sx={{ position: 'absolute', left: `${pctPos(t)}%`, top: 16, transform: 'translateX(-50%)' }}>
                                                    <Box sx={{ width: 1, height: 8, bgcolor: DARK_TEXT_DIM, mx: 'auto' }} />
                                                    <Typography sx={{ fontFamily, fontSize: '0.55rem', color: DARK_TEXT_DIM, textAlign: 'center', mt: 0.2, whiteSpace: 'nowrap' }}>
                                                        {t >= 1000 ? `${(t / 1000).toFixed(0)}k` : Math.round(t)}
                                                    </Typography>
                                                </Box>
                                            ))}

                                            {/* Current underlying price marker */}
                                            <Box sx={{
                                                position: 'absolute',
                                                left: `${Math.min(Math.max(pctPos(underlyingPrice), 2), 98)}%`,
                                                top: 14,
                                                transform: 'translateX(-50%)',
                                                zIndex: 2,
                                            }}>
                                                <Box sx={{ width: 2, height: 12, bgcolor: DARK_TEXT_MUTED, mx: 'auto' }} />
                                            </Box>

                                            {/* Leg strike indicators (clickable — centers view on strike) */}
                                            {legs.map((leg, idx) => {
                                                const pos = pctPos(leg.strike_price);
                                                if (pos < 0 || pos > 100) return null;
                                                const label = `${analyzerData?.position?.symbol || ''} ${Math.round(leg.strike_price)}${leg.option_type === 'CALL' ? 'C' : 'P'}`;
                                                const strikeColor = leg.side === 'BUY' ? '#22c55e' : '#ef4444';
                                                return (
                                                    <Tooltip key={idx} title={`Click to center view on $${leg.strike_price}`} placement="top" arrow>
                                                        <Box
                                                            sx={{
                                                                position: 'absolute',
                                                                left: `${Math.min(Math.max(pos, 3), 97)}%`,
                                                                top: 0,
                                                                transform: 'translateX(-50%)',
                                                                zIndex: 3,
                                                                cursor: 'pointer',
                                                                transition: 'transform 0.15s ease',
                                                                '&:hover': { transform: 'translateX(-50%) scale(1.12)' },
                                                            }}
                                                            onClick={() => {
                                                                // Re-center price range on this strike
                                                                const offset = ((leg.strike_price - underlyingPrice) / underlyingPrice) * 100;
                                                                const halfRange = (priceRange[1] - priceRange[0]) / 2;
                                                                setPriceRange([
                                                                    Math.max(Math.round(offset - halfRange), -50),
                                                                    Math.min(Math.round(offset + halfRange), 50),
                                                                ]);
                                                            }}
                                                        >
                                                            <Box sx={{
                                                                bgcolor: strikeColor,
                                                                color: '#fff',
                                                                fontFamily,
                                                                fontSize: '0.6rem',
                                                                fontWeight: 700,
                                                                px: 0.8,
                                                                py: 0.2,
                                                                borderRadius: '4px',
                                                                whiteSpace: 'nowrap',
                                                                lineHeight: 1.3,
                                                            }}>
                                                                {label}
                                                            </Box>
                                                            <Box sx={{ width: 1, height: 10, bgcolor: strikeColor, mx: 'auto' }} />
                                                        </Box>
                                                    </Tooltip>
                                                );
                                            })}
                                        </Box>
                                    </Box>
                                </Box>
                            );
                        })()}

                        {/* ── 3. Summary Bar (two-panel carousel) ────────── */}
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            bgcolor: DARK_HEADER,
                            px: 1,
                            py: 1.2,
                            borderBottom: `1px solid ${DARK_BORDER}`,
                            overflow: 'hidden',
                        }}>
                            <IconButton
                                size="small"
                                onClick={() => setSummaryPanel((p) => (p === 0 ? 1 : 0))}
                                sx={{ color: DARK_TEXT_MUTED, '&:hover': { color: DARK_TEXT } }}
                            >
                                <ChevronLeftIcon />
                            </IconButton>

                            <Box sx={{ flex: 1, overflow: 'hidden', position: 'relative' }}>
                                <Box sx={{
                                    display: 'flex',
                                    transition: 'transform 0.35s ease-in-out',
                                    transform: `translateX(-${summaryPanel * 100}%)`,
                                }}>
                                    {/* Panel 0: Strategy Summary (Net Debit, Max Loss, Max Profit, Chance of Profit, Breakeven) */}
                                    <Box sx={{ minWidth: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: { xs: 2.5, md: 5 } }}>
                                        {(() => {
                                            const sv = summaryValues || {};
                                            const underlyingPriceVal = analyzerData?.underlying_price || 0;

                                            let breakevenDisplay = '--';
                                            const breakevens = sv.breakevens || [];
                                            if (breakevens.length === 1) {
                                                const be = breakevens[0];
                                                const direction = be > underlyingPriceVal ? 'Above' : 'Below';
                                                const pctDiff = underlyingPriceVal > 0 ? (((be - underlyingPriceVal) / underlyingPriceVal) * 100).toFixed(1) : '0.0';
                                                breakevenDisplay = `${direction} $${parseFloat(be).toFixed(2)} (${pctDiff > 0 ? '+' : ''}${pctDiff}%)`;
                                            } else if (breakevens.length > 1) {
                                                breakevenDisplay = breakevens.map((be) => `$${parseFloat(be).toFixed(2)}`).join(' / ');
                                            }

                                            const maxLossDisplay = sv.maxLossAtEdge
                                                ? 'Unlimited'
                                                : sv.maxLoss != null ? `$${sv.maxLoss.toLocaleString('en-US', { minimumFractionDigits: 0 })}` : '--';

                                            return [
                                                {
                                                    label: sv.isDebit ? 'NET DEBIT:' : 'NET CREDIT:',
                                                    icon: '\uD83E\uDE99',
                                                    value: `$${Math.abs(sv.netPremium || 0).toLocaleString('en-US', { minimumFractionDigits: 0 })}`,
                                                    color: DARK_TEXT,
                                                },
                                                {
                                                    label: 'MAX LOSS:',
                                                    icon: '\u2193',
                                                    value: maxLossDisplay,
                                                    color: '#ef4444',
                                                },
                                                {
                                                    label: 'MAX PROFIT:',
                                                    icon: '\u2197',
                                                    value: sv.maxProfit != null ? `$${sv.maxProfit.toLocaleString('en-US', { minimumFractionDigits: 0 })}` : '--',
                                                    color: '#22c55e',
                                                },
                                                {
                                                    label: 'CHANCE OF PROFIT:',
                                                    icon: '\uD83C\uDFB0',
                                                    value: sv.chanceOfProfit != null ? `${sv.chanceOfProfit}%` : '--%',
                                                    color: sv.chanceOfProfit != null ? DARK_TEXT : DARK_TEXT_MUTED,
                                                },
                                                {
                                                    label: 'BREAKEVEN:',
                                                    icon: '\u2192',
                                                    value: breakevenDisplay,
                                                    color: DARK_TEXT,
                                                },
                                            ].map((item) => (
                                                <Box key={item.label} sx={{ textAlign: 'center' }}>
                                                    <Typography sx={{ fontFamily, fontSize: '0.6rem', fontWeight: 600, color: DARK_TEXT_MUTED, letterSpacing: '0.06em' }}>
                                                        {item.label}
                                                    </Typography>
                                                    <Typography sx={{ fontFamily, fontSize: '0.95rem', fontWeight: 700, color: item.color, whiteSpace: 'nowrap' }}>
                                                        <span style={{ marginRight: 4 }}>{item.icon}</span>
                                                        {item.value}
                                                        {item.valueExtra && <span style={{ marginLeft: 4, fontSize: '0.75rem' }}>{item.valueExtra}</span>}
                                                    </Typography>
                                                </Box>
                                            ));
                                        })()}
                                    </Box>

                                    {/* Panel 1: Greeks */}
                                    <Box sx={{ minWidth: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: { xs: 3, md: 6 } }}>
                                        {[
                                            { label: 'DELTA:', symbol: '\u0394', value: matrixResult.netGreeks?.delta, fmt: (v) => v?.toFixed(1) },
                                            { label: 'THETA:', symbol: '\u0398', value: matrixResult.netGreeks?.theta, fmt: (v) => v?.toFixed(1) },
                                            { label: 'GAMMA:', symbol: '\u0393', value: matrixResult.netGreeks?.gamma, fmt: (v) => v?.toFixed(2) },
                                            { label: 'VEGA:', symbol: '\u03BD', value: matrixResult.netGreeks?.vega, fmt: (v) => v?.toFixed(1) },
                                            { label: 'RHO:', symbol: '\u03C1', value: matrixResult.netGreeks?.rho, fmt: (v) => v?.toFixed(2) },
                                        ].map((g) => (
                                            <Box key={g.label} sx={{ textAlign: 'center' }}>
                                                <Typography sx={{ fontFamily, fontSize: '0.65rem', fontWeight: 600, color: DARK_TEXT_MUTED, letterSpacing: '0.06em' }}>
                                                    {g.label}
                                                </Typography>
                                                <Typography sx={{ fontFamily, fontSize: '1.1rem', fontWeight: 700, color: DARK_TEXT }}>
                                                    <span style={{ color: ACCENT }}>{g.symbol}</span>{' '}
                                                    {g.value != null ? g.fmt(g.value) : '--'}
                                                </Typography>
                                            </Box>
                                        ))}
                                    </Box>
                                </Box>
                            </Box>

                            <IconButton
                                size="small"
                                onClick={() => setSummaryPanel((p) => (p === 0 ? 1 : 0))}
                                sx={{ color: DARK_TEXT_MUTED, '&:hover': { color: DARK_TEXT } }}
                            >
                                <ChevronRightIcon />
                            </IconButton>
                        </Box>

                        {/* ── Graph View ──────────────────────────────── */}
                        {displayType === 'graph' && (
                            <PayoffGraph
                                prices={matrixResult.prices}
                                matrix={matrixResult.matrix}
                                underlyingPrice={analyzerData.underlying_price}
                            />
                        )}

                        {/* ── Heatmap Table (native HTML for full control) ─ */}
                        {displayType === 'table' && <Box sx={{
                            maxHeight: 580,
                            overflow: 'auto',
                            '&::-webkit-scrollbar': { width: 6, height: 6 },
                            '&::-webkit-scrollbar-track': { bgcolor: DARK_BG },
                            '&::-webkit-scrollbar-thumb': { bgcolor: '#475569', borderRadius: 3 },
                        }}>
                            <table
                                style={{
                                    borderCollapse: 'collapse',
                                    width: '100%',
                                    tableLayout: 'fixed',
                                }}
                            >
                                <thead>
                                    {/* Row 1: Month groups */}
                                    <tr>
                                        <th
                                            style={{
                                                ...headerCellBase,
                                                minWidth: 85,
                                                width: 85,
                                                borderBottom: 'none',
                                                zIndex: 3,
                                                left: 0,
                                            }}
                                        />
                                        {monthGroups.map((group, idx) => (
                                            <th
                                                key={idx}
                                                colSpan={group.count}
                                                style={{
                                                    ...headerCellBase,
                                                    fontSize: '0.72rem',
                                                    fontWeight: 400,
                                                    color: DARK_TEXT_MUTED,
                                                    borderBottom: 'none',
                                                    letterSpacing: '0.02em',
                                                }}
                                            >
                                                {group.label}
                                            </th>
                                        ))}
                                    </tr>
                                    {/* Row 2: Day number + day-of-week */}
                                    <tr>
                                        <th
                                            style={{
                                                ...headerCellBase,
                                                top: 28,
                                                minWidth: 85,
                                                width: 85,
                                                zIndex: 3,
                                                left: 0,
                                            }}
                                        />
                                        {matrixResult.dates.map((dateStr, colIdx) => {
                                            const d = safeParseDateStr(dateStr);
                                            return (
                                                <th
                                                    key={colIdx}
                                                    style={{
                                                        ...headerCellBase,
                                                        top: 28,
                                                        fontSize: '0.72rem',
                                                        fontWeight: 700,
                                                        color: DARK_TEXT,
                                                    }}
                                                >
                                                    {d.getDate()}{' '}
                                                    <span style={{ fontWeight: 400, fontSize: '0.62rem', color: DARK_TEXT_MUTED }}>
                                                        {DAY_LABELS[d.getDay()]}
                                                    </span>
                                                </th>
                                            );
                                        })}
                                    </tr>
                                </thead>
                                <tbody>
                                    {matrixResult.prices.map((price, rowIdx) => {
                                        const pctChange = (
                                            ((price - analyzerData.underlying_price) / analyzerData.underlying_price) * 100
                                        ).toFixed(1);
                                        const pctNum = parseFloat(pctChange);

                                        return (
                                            <tr key={rowIdx}>
                                                {/* Sticky price column */}
                                                <td
                                                    style={{
                                                        fontFamily,
                                                        fontSize: '0.78rem',
                                                        fontWeight: 700,
                                                        color: DARK_TEXT,
                                                        backgroundColor: DARK_HEADER,
                                                        padding: '5px 8px',
                                                        whiteSpace: 'nowrap',
                                                        position: 'sticky',
                                                        left: 0,
                                                        zIndex: 1,
                                                        borderRight: `1px solid ${DARK_BORDER}`,
                                                        borderBottom: `1px solid ${DARK_BG}`,
                                                        minWidth: 85,
                                                        width: 85,
                                                    }}
                                                >
                                                    ${price.toFixed(0)}
                                                    <span
                                                        style={{
                                                            fontSize: '0.6rem',
                                                            fontWeight: 500,
                                                            marginLeft: 3,
                                                            color: pctNum > 0 ? '#f59e0b' : pctNum < 0 ? '#f59e0b' : DARK_TEXT_DIM,
                                                        }}
                                                    >
                                                        {pctNum > 0 ? '+' : ''}{pctChange}%
                                                    </span>
                                                </td>

                                                {/* Data cells */}
                                                {displayData.displayMatrix[rowIdx].map((cellVal, colIdx) => {
                                                    const colors = getCellColor(
                                                        cellVal,
                                                        displayData.maxPositive,
                                                        displayData.maxNegative
                                                    );

                                                    // Red dashed line at profit/loss boundary
                                                    const nextRow = displayData.displayMatrix[rowIdx + 1];
                                                    const isBoundary = nextRow && cellVal >= 0 && nextRow[colIdx] < 0;

                                                    return (
                                                        <td
                                                            key={colIdx}
                                                            style={{
                                                                ...cellBase,
                                                                backgroundColor: colors.bg,
                                                                color: colors.text,
                                                                borderBottom: isBoundary
                                                                    ? '2px dashed #ef4444'
                                                                    : `1px solid ${DARK_BG}`,
                                                            }}
                                                        >
                                                            {formatCellValue(cellVal, viewMode)}
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </Box>}

                        {/* ── Bottom Controls ─────────────────────────── */}
                        <Box sx={{ px: 3, py: 2, borderTop: `1px solid ${DARK_BORDER}` }}>
                            {/* Range Slider */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
                                <Typography sx={{ fontFamily, fontSize: '0.75rem', fontWeight: 700, color: DARK_TEXT_MUTED, minWidth: 55, letterSpacing: '0.03em' }}>
                                    RANGE:
                                </Typography>
                                <Typography sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 700, color: DARK_TEXT, minWidth: 50 }}>
                                    {`\u00B1${Math.abs(priceRange[1])}%`}
                                </Typography>
                                <Slider
                                    value={priceRange}
                                    onChange={(_, v) => setPriceRange(v)}
                                    min={-50}
                                    max={50}
                                    valueLabelDisplay="auto"
                                    valueLabelFormat={(v) => `${v > 0 ? '+' : ''}${v}%`}
                                    sx={{
                                        color: '#3b82f6',
                                        flex: 1,
                                        '& .MuiSlider-thumb': { width: 14, height: 14, bgcolor: '#fff' },
                                        '& .MuiSlider-track': { height: 4 },
                                        '& .MuiSlider-rail': { height: 4, bgcolor: DARK_BORDER },
                                    }}
                                />
                                <Tooltip title="Reset range">
                                    <IconButton size="small" onClick={() => setPriceRange([-23, 23])} sx={{ color: DARK_TEXT_DIM }}>
                                        <ResetIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Box>

                            {/* IV Slider */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Typography sx={{ fontFamily, fontSize: '0.75rem', fontWeight: 700, color: DARK_TEXT_MUTED, minWidth: 160, letterSpacing: '0.03em' }}>
                                    IMPLIED VOLATILITY:
                                </Typography>
                                <Typography sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 700, color: DARK_TEXT, minWidth: 45 }}>
                                    {`${((matrixResult?.avgIV || 0.3) * ivMultiplier * 100).toFixed(0)}%`}
                                </Typography>
                                <Slider
                                    value={ivMultiplier}
                                    onChange={(_, v) => setIvMultiplier(v)}
                                    min={0.1}
                                    max={3.0}
                                    step={0.05}
                                    marks={[
                                        { value: 1, label: '\u00D71' },
                                        { value: 2, label: '\u00D72' },
                                        { value: 3, label: '\u00D73' },
                                    ]}
                                    valueLabelDisplay="auto"
                                    valueLabelFormat={(v) => `\u00D7${v.toFixed(1)}`}
                                    sx={{
                                        color: '#3b82f6',
                                        flex: 1,
                                        '& .MuiSlider-thumb': { width: 14, height: 14, bgcolor: '#fff' },
                                        '& .MuiSlider-track': { height: 4 },
                                        '& .MuiSlider-rail': { height: 4, bgcolor: DARK_BORDER },
                                        '& .MuiSlider-markLabel': { fontFamily, fontSize: '0.65rem', color: DARK_TEXT_DIM },
                                    }}
                                />
                                <Tooltip title="Reset IV">
                                    <IconButton size="small" onClick={() => setIvMultiplier(1.0)} sx={{ color: DARK_TEXT_DIM }}>
                                        <ResetIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </Box>

                        {/* ── View Mode Tabs ──────────────────────────── */}
                        <Box sx={{
                            px: 2,
                            py: 1.2,
                            borderTop: `1px solid ${DARK_BORDER}`,
                            bgcolor: DARK_HEADER,
                            borderRadius: '0 0 16px 16px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            flexWrap: 'wrap',
                        }}>
                            {/* P&L view mode toggles */}
                            {[
                                { mode: 'pnl_dollar', label: 'Profit / Loss $' },
                                { mode: 'pnl_percent', label: 'Profit / Loss %' },
                                { mode: 'contract_value', label: 'Contract Value' },
                                { mode: 'pct_max_risk', label: '% of Max Risk' },
                            ].map((tab) => {
                                const isActive = viewMode === tab.mode && displayType === 'table';
                                return (
                                    <Chip
                                        key={tab.mode}
                                        label={tab.label}
                                        onClick={() => { setViewMode(tab.mode); setDisplayType('table'); }}
                                        sx={{
                                            fontFamily, fontSize: '0.73rem', fontWeight: isActive ? 700 : 500, height: 32,
                                            bgcolor: isActive ? '#10b981' : 'transparent',
                                            color: isActive ? '#fff' : DARK_TEXT_MUTED,
                                            border: isActive ? 'none' : `1px solid ${DARK_BORDER}`,
                                            borderRadius: '8px', cursor: 'pointer',
                                            '&:hover': { bgcolor: isActive ? '#10b981' : DARK_BORDER },
                                        }}
                                    />
                                );
                            })}
                        </Box>
                    </Box>
                )}

                {/* ── Position Info Summary ────────────────────────────── */}
                {!dataLoading && analyzerData && (
                    <Box sx={{ mt: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <Chip label={`Symbol: ${analyzerData.position?.symbol || '--'}`} sx={{ fontFamily, fontWeight: 600, bgcolor: '#f1f5f9', color: '#1e293b' }} />
                        <Chip label={`Strategy: ${StrategyTypeLabels?.[analyzerData.position?.strategy_type] || analyzerData.position?.strategy_type || '--'}`} sx={{ fontFamily, fontWeight: 600, bgcolor: '#f1f5f9', color: '#1e293b' }} />
                        <Chip label={`Underlying: $${analyzerData.underlying_price?.toFixed(2) || '--'}`} sx={{ fontFamily, fontWeight: 600, bgcolor: '#f1f5f9', color: '#1e293b' }} />
                        <Chip label={`Legs: ${analyzerData.legs?.length || 0}`} sx={{ fontFamily, fontWeight: 600, bgcolor: '#f1f5f9', color: '#1e293b' }} />
                        {analyzerData.position?.max_profit != null && (
                            <Chip label={`Max Profit: $${analyzerData.position.max_profit.toFixed(0)}`} sx={{ fontFamily, fontWeight: 600, bgcolor: '#dcfce7', color: '#166534' }} />
                        )}
                        {analyzerData.position?.max_loss != null && (
                            <Chip label={`Max Loss: $${Math.abs(analyzerData.position.max_loss).toFixed(0)}`} sx={{ fontFamily, fontWeight: 600, bgcolor: '#fee2e2', color: '#991b1b' }} />
                        )}
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default StrategyAnalyzerPage;
